import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import moment from 'moment';
import { FiPrinter, FiMail, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import Stock from '../../../model/Stock';
import { startSetStock } from '../../../actions/CurrentStock';
import EmptyState from '../EmptyState';
import { displayName, initials } from '../Header';
import { providers } from './AllHolders';
import { EmailReportModal } from './ReportModals';
import {
    ReportKey, RegistryData, isReportKey, useRegistryData, asAtDate, num, pct, signed,
    sharesOnIssue, holderCount, emailCount, topRows, bands, moves, moveCounts, substantial, Moves,
} from './reportsData';

// /registry-report/{ticker:exchange}/{key}[?totals=0], opened in a new tab from Share Registry › Reports › Download › PDF.
// Like InvoiceView: no header (AppRouter headerlessRoutes), A4 sheets, Print / Save as PDF uses the browser's print dialog.
// Figma 534:3006 (Top 20 PDF) and 548:1913…2574 (board pack p1–p5). All figures come from the register (reportsData.ts);
// anything without a source shows "—" or "Not connected yet".

interface Ctx { stock: Stock; data: RegistryData; totals: boolean; company: string; provider: string; asAt: string | null; issued: number }

const dash = '—';

const Sheet: React.FC<{ ctx: Ctx; label: string; page: number; pages: number }> = ({ ctx, label, page, pages, children }) => {
    const user = useSelector((state: IGlobalState) => state.user);
    return <article className="rr-sheet">
        <header className="rr-running">
            {ctx.stock.companyLogoUrl
                ? <img className="rr-logo" src={ctx.stock.companyLogoUrl} alt="" />
                : <span className="rr-logo rr-initials">{initials(ctx.company)}</span>}
            <strong>{ctx.company.toUpperCase()}</strong>
            <span className="rr-label">{label.toUpperCase()}</span>
        </header>
        <div className="rr-rule" />
        <div className="rr-content">{children}</div>
        <footer className="rr-foot">
            <span>Registry data supplied by {ctx.provider} · Confidential — for the board and advisers of {ctx.company} · Generated {moment().format('D MMMM YYYY')} by {displayName(user)}</span>
            <strong>Page {page} of {pages}</strong>
        </footer>
    </article>;
};

const Kpi: React.FC<{ v: string; k: string; d?: React.ReactNode; tone?: 'up' | 'down' }> = ({ v, k, d, tone }) =>
    <div className="rr-kpi"><strong>{v}</strong><span>{k}</span>{d && <span className={`rr-kpi-d${tone ? ` ${tone}` : ''}`}>{d}</span>}</div>;

const Missing: React.FC<{ text: string }> = ({ text }) => <div className="rr-missing"><FiAlertCircle /> {text}</div>;

const ptsCell = (p: number | null) => p === null || Math.abs(p) < 0.05
    ? <span className="rr-muted">{dash}</span>
    : <span className={p > 0 ? 'rr-up' : 'rr-down'}>{signed(p, 1)}</span>;

const Top20: React.FC<{ ctx: Ctx }> = ({ ctx }) => {
    const rows = topRows(ctx.stock, ctx.data);
    const top20 = rows.reduce((s, r) => s + r.pct, 0);
    const shares = rows.reduce((s, r) => s + r.holding, 0);
    const holders = holderCount(ctx.stock, ctx.data.listing);
    const emails = emailCount(ctx.stock, ctx.data.listing);
    return <>
        <div className="rr-h2">
            <h2>Top 20 shareholders</h2>
            <p>{[ctx.asAt && `Register as at ${ctx.asAt}`, `supplied by ${ctx.provider}`].filter(Boolean).join(', ')}{ctx.issued ? ` · ${num(ctx.issued)} shares on issue` : ''}</p>
        </div>
        {ctx.totals && <div className="rr-kpis">
            <Kpi v={holders === null ? dash : num(holders)} k="holders on the register" />
            <Kpi v={rows.length ? pct(top20, 1) : dash} k="held by the top 20" />
            <Kpi v={emails === null ? dash : num(emails)} k="holders with an email" />
        </div>}
        {!ctx.data.top ? <Missing text="The top holders report isn’t available for this company yet." /> : <>
            <div className="rr-table rr-top">
                <div className="rr-row rr-head"><span>#</span><span>Holder</span><span className="rr-num">Shares</span><span className="rr-num">% Held</span><span className="rr-num">Chg (pts)</span></div>
                {rows.map(r => <div className="rr-row" key={`${r.position}:${r.name}`}>
                    <span className="rr-muted">{r.position}</span><span className="rr-name">{r.name}</span>
                    <span className="rr-num rr-sec">{num(r.holding)}</span><span className="rr-num rr-sec">{pct(r.pct)}</span><span className="rr-num">{ptsCell(r.pts)}</span>
                </div>)}
            </div>
            {ctx.totals && <div className="rr-total"><span>Top 20 total</span><strong>{num(shares)}</strong><strong className="rr-cyan">{pct(top20)}</strong></div>}
        </>}
    </>;
};

const HoldersBySize: React.FC<{ ctx: Ctx }> = ({ ctx }) => {
    const b = bands(ctx.stock, ctx.data.listing);
    if (!b) return <><div className="rr-h2"><h2>Holders by size</h2></div><Missing text="The register listing isn’t available for this company yet." /></>;
    const holders = b.reduce((s, x) => s + x.holders, 0);
    const shares = b.reduce((s, x) => s + x.shares, 0);
    const under5k = b[0].holdersPct + b[1].holdersPct;
    const widest = Math.max(...b.map(x => x.holdersPct), 1);
    return <>
        <div className="rr-h2">
            <h2>Holders by size</h2>
            <p>The ASX calls this a distribution schedule. It goes in the annual report and shows how widely the shares are spread.</p>
        </div>
        {ctx.totals && <div className="rr-kpis">
            <Kpi v={num(holders)} k="holders in total" />
            <Kpi v={dash} k="hold less than a marketable parcel" d="Needs the share price — not connected yet" />
            <Kpi v={pct(under5k, 1)} k="of holders own under 5,000 shares" />
        </div>}
        <div className="rr-table rr-bands">
            <div className="rr-row rr-head"><span>Holding range</span><span className="rr-num">Holders</span><span className="rr-num">% of holders</span><span className="rr-num">Shares</span><span className="rr-num">% of shares</span></div>
            {b.map(x => <div className="rr-row" key={x.label}>
                <span className="rr-sec">{x.label}</span><span className="rr-num rr-strong">{num(x.holders)}</span><span className="rr-num rr-sec">{pct(x.holdersPct, 1)}</span>
                <span className="rr-num rr-sec">{num(x.shares)}</span><span className="rr-num rr-sec">{pct(x.sharesPct)}</span>
            </div>)}
        </div>
        {ctx.totals && <div className="rr-total"><span>Total</span><strong>{num(holders)} holders</strong><strong className="rr-cyan">{num(shares)} shares</strong></div>}
        <div className="rr-panel">
            <h3>How many holders in each band</h3>
            {b.map(x => <div className="rr-bar" key={x.label}>
                <span className="rr-sec">{x.label}</span>
                <span className="rr-track"><span style={{ width: `${x.holdersPct / widest * 100}%` }} /></span>
                <strong>{num(x.holders)} ({pct(x.holdersPct, 1)})</strong>
            </div>)}
        </div>
    </>;
};

const MoveTable: React.FC<{ m: Moves; limit: number }> = ({ m, limit }) => <div className="rr-table rr-moves">
    <div className="rr-row rr-head"><span>Holder</span><span>What happened</span><span className="rr-num">Change</span><span className="rr-num">Now holds</span><span className="rr-num">% Held</span></div>
    {m.rows.slice(0, limit).map(r => <div className="rr-row" key={r.holderId}>
        <span className="rr-sec rr-name">{r.name}</span><span className="rr-strong">{r.kind}</span>
        <span className={`rr-num ${r.change > 0 ? 'rr-up' : 'rr-down'}`}>{signed(r.change)}</span>
        <span className="rr-num rr-sec">{num(r.now)}</span><span className="rr-num rr-sec">{r.now ? pct(r.pct) : dash}</span>
    </div>)}
    {!m.rows.length && <p className="rr-empty">No holding changed in this period.</p>}
</div>;

const WhoBoughtAndSold: React.FC<{ ctx: Ctx; limit: number }> = ({ ctx, limit }) => {
    const m = moves(ctx.stock, ctx.data);
    if (!m) return <><div className="rr-h2"><h2>Who bought and sold</h2></div><Missing text="Available once 30 days of register history exist." /></>;
    const c = moveCounts(m);
    return <>
        <div className="rr-h2">
            <h2>Who bought and sold — last 30 days</h2>
            <p>Every holding that changed between {moment(m.from).format('D MMMM')} and {m.to ? moment(m.to).format('D MMMM YYYY') : 'the latest sync'}, largest change first.</p>
        </div>
        {ctx.totals && <div className="rr-kpis four">
            <Kpi v={num(c.up)} k="holders bought more" d={`${signed(c.upShares)} shares`} tone="up" />
            <Kpi v={num(c.down)} k="holders sold down" d={`${signed(c.downShares)} shares`} tone="down" />
            <Kpi v={num(c.joined)} k="new holders joined" />
            <Kpi v={num(c.left)} k="holders left the register" />
        </div>}
        <MoveTable m={m} limit={limit} />
        {m.rows.length > limit && <p className="rr-note">Showing the {limit} largest of {m.rows.length} changes. The CSV download has every one.</p>}
    </>;
};

const Substantial: React.FC<{ ctx: Ctx }> = ({ ctx }) => {
    const rows = substantial(ctx.stock, ctx.data);
    return <>
        <div className="rr-h2">
            <h2>Substantial holders</h2>
            <p>Anyone holding 5% or more must lodge a notice with the ASX. This is where you check that they have.</p>
        </div>
        {!ctx.data.top ? <Missing text="The top holders report isn’t available for this company yet." /> : <div className="rr-table rr-subs">
            <div className="rr-row rr-head"><span>Holder</span><span className="rr-num">Holding</span><span className="rr-num">% Held</span><span>Last notice</span><span>Status</span></div>
            {rows.map(r => <div className="rr-row" key={`${r.position}:${r.name}`}>
                <span className="rr-sec rr-name">{r.name}</span><span className="rr-num rr-strong">{num(r.holding)}</span><span className="rr-num rr-sec">{pct(r.pct)}</span>
                <span className="rr-muted">{dash}</span><span className="rr-muted">{dash}</span>
            </div>)}
            {!rows.length && <p className="rr-empty">No holder has 5% or more.</p>}
        </div>}
        <p className="rr-note">Form 603 / 604 notices aren’t connected yet, so Last notice and Status show “—”.</p>
    </>;
};

const Summary: React.FC<{ ctx: Ctx; month: string }> = ({ ctx, month }) => {
    const user = useSelector((state: IGlobalState) => state.user);
    const holders = holderCount(ctx.stock, ctx.data.listing);
    const emails = emailCount(ctx.stock, ctx.data.listing);
    const rows = topRows(ctx.stock, ctx.data);
    const top20 = rows.reduce((s, r) => s + r.pct, 0);
    const top20Pts = rows.some(r => r.pts !== null) ? rows.reduce((s, r) => s + (r.pts ?? 0), 0) : null;
    const m = moves(ctx.stock, ctx.data);
    const c = m ? moveCounts(m) : null;
    const subs = substantial(ctx.stock, ctx.data);
    const biggest = m?.rows[0];
    const noEmail = holders !== null && emails !== null ? holders - emails : null;
    const emailShare = holders && emails !== null ? emails / holders * 100 : null;

    // the paragraph is put together from the figures above, nothing else
    const sentences = [
        holders !== null && `The register has ${num(holders)} holders${ctx.asAt ? ` as at ${ctx.asAt}` : ''}.`,
        rows.length > 0 && `The top 20 hold ${pct(top20, 1)} of the shares on issue.`,
        c && `Over the last 30 days ${num(c.up)} holders bought more and ${num(c.down)} sold down; ${num(c.joined)} new holders joined and ${num(c.left)} left the register.`,
        biggest && `The largest change was ${biggest.name} (${signed(biggest.change)} shares, now ${pct(biggest.pct)}).`,
        ctx.data.top && `${subs.length} holder${subs.length === 1 ? '' : 's'} hold${subs.length === 1 ? 's' : ''} 5% or more.`,
    ].filter(Boolean);

    const lookAt = [
        rows[0] && rows[0].pct >= 15 && rows[0].pct < 20 && { t: `${rows[0].name} is approaching 20%`, d: `Now ${pct(rows[0].pct)} of the shares on issue.` },
        noEmail !== null && noEmail > 0 && { t: `${num(noEmail)} holders have no email address`, d: 'Every one of them has to be posted to. Asking them to sign up would cut print and postage and get announcements to them faster.' },
    ].filter(Boolean) as { t: string; d: string }[];

    return <>
        <div className="rr-h2">
            <h2>Share register — {month}</h2>
            <p>Prepared for the board of {ctx.company}{ctx.asAt ? ` · Register as at ${ctx.asAt}` : ''}, supplied by {ctx.provider}{ctx.issued ? ` · ${num(ctx.issued)} shares on issue` : ''}</p>
        </div>
        <div className="rr-kpis four">
            <Kpi v={holders === null ? dash : num(holders)} k="holders on the register" />
            <Kpi v={rows.length ? pct(top20, 1) : dash} k="held by the top 20" d={top20Pts === null ? undefined : `${signed(top20Pts, 1)} pts in 30 days`} tone={top20Pts !== null && top20Pts >= 0 ? 'up' : 'down'} />
            <Kpi v={emails === null ? dash : num(emails)} k="holders with an email" d={emailShare === null ? undefined : `${pct(emailShare, 0)} of the register`} />
            <Kpi v={m ? num(m.rows.length) : dash} k="holdings changed" d={c ? `${c.up + c.joined} up · ${c.down + c.left} down` : 'needs 30 days of history'} />
        </div>
        <div className="rr-paragraph">
            <h3>The month in a paragraph</h3>
            <p>{sentences.length ? sentences.join(' ') : dash}</p>
        </div>
        {lookAt.length > 0 && <div className="rr-look">
            <h3>{lookAt.length === 1 ? 'One thing' : 'Two things'} worth a look</h3>
            {lookAt.map(x => <div key={x.t} className="rr-look-item"><FiAlertCircle /><div><strong>{x.t}</strong><span>{x.d}</span></div></div>)}
        </div>}
        <div className="rr-panel rr-contents">
            <h3>What’s in this pack</h3>
            {['Top 20 shareholders', 'Holders by size (distribution schedule)', 'Who bought and sold in the last 30 days', 'Substantial holders, announcements and email results'].map((x, i) =>
                <div key={x} className="rr-contents-row"><FiCheck /><span>{x}</span><strong>Page {i + 2}</strong></div>)}
        </div>
        <p className="rr-note">Made on {moment().format('D MMMM YYYY')} by {displayName(user)}, with Investi.</p>
    </>;
};

const Announcements: React.FC = () => <>
    <div className="rr-h2 rr-h2-gap">
        <h2>Announcements and email results</h2>
        <p>How the market and your shareholders responded this month.</p>
    </div>
    <Missing text="Not connected yet — announcement, email open-rate and sign-up figures will appear here once the board pack is wired to Communications." />
</>;

const ReportView: React.FC = () => {
    const dispatch = useDispatch();
    const { ticker, report } = useParams<{ ticker: string; report: string }>();
    const location = useLocation();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const loaded = !!stock && `${stock.ticker}:${stock.exchange}` === ticker;
    const data = useRegistryData(loaded ? stock : null);
    const [emailing, setEmailing] = useState(false);

    // no header here to load the current client, so load the one in the url
    useEffect(() => {
        if (!loaded) dispatch(startSetStock(ticker));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticker]);

    if (!loaded) return null;

    if (!isReportKey(report))
        return <div className="v2-page registry-report-view">
            <EmptyState art="caughtUp" title="Report not found" sub="Make it again from Share Registry › Reports." />
        </div>;

    const key: ReportKey = report;
    const company = stock.companyName || stock.ticker;
    const asAt = asAtDate(data.listing);
    const providerName = providers[stock.shareRegistryProvider ?? 0];
    const ctx: Ctx = {
        stock, data, company, asAt,
        totals: new URLSearchParams(location.search).get('totals') !== '0',
        provider: providerName && providerName !== 'None' ? providerName : 'the share registry',
        issued: sharesOnIssue(stock, data),
    };
    const month = moment().format('MMMM YYYY');
    const shortDate = data.listing?.asAt ? moment(data.listing.asAt).format('D MMM YYYY') : moment().format('D MMM YYYY');
    const single = (label: string, body: React.ReactNode) => <Sheet ctx={ctx} label={`${label} · ${shortDate}`} page={1} pages={1}>{body}</Sheet>;
    const packLabel = `${moment().format('MMMM')} board pack · ${shortDate}`;

    const sheets = () => {
        if (key === 'top-20') return single('Top 20 shareholders', <Top20 ctx={ctx} />);
        if (key === 'holders-by-size') return single('Holders by size', <HoldersBySize ctx={ctx} />);
        if (key === 'who-bought-and-sold') return single('Who bought and sold', <WhoBoughtAndSold ctx={ctx} limit={20} />);
        if (key === 'substantial-holders') return single('Substantial holders', <Substantial ctx={ctx} />);
        return <>
            <Sheet ctx={ctx} label={packLabel} page={1} pages={5}><Summary ctx={ctx} month={month} /></Sheet>
            <Sheet ctx={ctx} label={packLabel} page={2} pages={5}><Top20 ctx={ctx} /></Sheet>
            <Sheet ctx={ctx} label={packLabel} page={3} pages={5}><HoldersBySize ctx={ctx} /></Sheet>
            <Sheet ctx={ctx} label={packLabel} page={4} pages={5}><WhoBoughtAndSold ctx={ctx} limit={10} /></Sheet>
            <Sheet ctx={ctx} label={packLabel} page={5} pages={5}><Substantial ctx={ctx} /><Announcements /></Sheet>
        </>;
    };

    return <div className="v2-page registry-report-view">
        <div className="rr-toolbar">
            <span className="rr-file">{stock.ticker}-{key}.pdf</span>
            <button type="button" className="btn-outline" onClick={() => setEmailing(true)}><FiMail /> Email This Report</button>
            <button type="button" className="btn-primary" onClick={() => window.print()} disabled={data.loading}><FiPrinter /> Print / Save as PDF</button>
        </div>
        {data.loading && <p className="rr-loading">Loading the register… (first load can take a minute while reports generate)</p>}
        {data.error && <p className="error">{data.error}</p>}
        {!data.loading && <div className="rr-doc">{sheets()}</div>}
        {emailing && <EmailReportModal stock={stock} report={key} asAt={asAt} onClose={() => setEmailing(false)} />}
    </div>;
};

export default ReportView;
